import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, authHeaders } from "@/lib/api";
import { Save } from "lucide-react";

export default function SeatsManager() {
  const [state, setState] = useState({
    total: 15,
    use_manual: false,
    manual_claimed: 0,
    auto_claimed: 0,
    effective_claimed: 0,
  });
  const [total, setTotal] = useState("15");
  const [manual, setManual] = useState("0");
  const [useManual, setUseManual] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchState = async () => {
    try {
      const res = await api.get("/admin/seats", { headers: authHeaders() });
      setState(res.data);
      setTotal(String(res.data.total));
      setManual(String(res.data.manual_claimed ?? res.data.auto_claimed));
      setUseManual(!!res.data.use_manual);
    } catch {
      toast.error("Failed to load seat state.");
    }
  };

  useEffect(() => {
    fetchState();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post(
        "/admin/seats",
        {
          total: parseInt(total, 10),
          manual_claimed: parseInt(manual, 10),
          use_manual: useManual,
        },
        { headers: authHeaders() }
      );
      setState(res.data);
      toast.success("Seat counter updated.");
    } catch {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const pct = state.total > 0 ? Math.min(100, (state.effective_claimed / state.total) * 100) : 0;

  return (
    <section
      className="border p-6 sm:p-8 mb-12"
      style={{
        background: "rgba(48,23,40,0.55)",
        borderColor: "rgba(226,211,226,0.22)",
        borderRadius: "8px",
      }}
      data-testid="seats-manager"
    >
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="small-caps mb-2" style={{ color: "#B9929D" }}>
            Seats progress bar
          </div>
          <h2 className="font-display" style={{ fontSize: "32px", color: "#F2EEE7", lineHeight: 1.1 }}>
            The <em style={{ color: "#DDBFCF" }}>counter</em> guests see
          </h2>
        </div>
        <span className="text-xs italic" style={{ color: "#DDBFCF", opacity: 0.7 }}>
          Auto count: <strong>{state.auto_claimed}</strong> · Showing: <strong>{state.effective_claimed}</strong> / {state.total}
        </span>
      </div>

      {/* Live preview */}
      <div className="mb-8">
        <div
          className="small-caps mb-3"
          style={{ color: "#DDBFCF", opacity: 0.7 }}
          data-testid="seats-preview-label"
        >
          {state.effective_claimed} of {state.total} seats claimed
        </div>
        <div
          style={{
            height: "3px",
            width: "100%",
            maxWidth: "320px",
            background: "rgba(226,211,226,0.18)",
            borderRadius: "2px",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${pct}%`,
              background: "#b8c975",
              transition: "width 400ms ease",
            }}
          />
        </div>
      </div>

      <form onSubmit={handleSave} className="grid grid-cols-1 sm:grid-cols-3 gap-6 items-end">
        <div>
          <label className="block small-caps mb-2" style={{ color: "#B9929D" }} htmlFor="seats-total">
            Total seats
          </label>
          <input
            id="seats-total"
            data-testid="seats-total-input"
            type="number"
            min="1"
            className="kynn-input"
            value={total}
            onChange={(e) => setTotal(e.target.value)}
          />
        </div>

        <div>
          <label className="block small-caps mb-2" style={{ color: "#B9929D" }} htmlFor="seats-manual">
            Manual claimed
          </label>
          <input
            id="seats-manual"
            data-testid="seats-manual-input"
            type="number"
            min="0"
            className="kynn-input"
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            disabled={!useManual}
            style={{ opacity: useManual ? 1 : 0.4 }}
          />
        </div>

        <div className="flex flex-col gap-3">
          <label
            className="small-caps flex items-center gap-2 cursor-pointer"
            style={{ color: "#F2EEE7" }}
          >
            <input
              type="checkbox"
              checked={useManual}
              onChange={(e) => setUseManual(e.target.checked)}
              data-testid="seats-use-manual-toggle"
              style={{ accentColor: "#b8c975" }}
            />
            Use manual override
          </label>
          <button
            type="submit"
            disabled={saving}
            data-testid="seats-save-btn"
            className="btn-primary"
            style={{ justifyContent: "center" }}
          >
            <Save size={14} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>

      <p className="mt-6 text-xs italic" style={{ color: "#DDBFCF", opacity: 0.6, lineHeight: 1.6 }}>
        When <span className="not-italic small-caps">manual override</span> is off, the bar shows live approved + VIP counts. Turn it on to hand-set the number displayed publicly.
      </p>
    </section>
  );
}
